import type { PlayerInput } from '../shared/entities.ts';
import type { PlayerAction } from '../shared/protocol.ts';

const KEYS: Record<string, keyof PlayerInput> = {
  KeyW: 'up',
  KeyA: 'left',
  KeyS: 'down',
  KeyD: 'right',
  ArrowUp: 'up',
  ArrowLeft: 'left',
  ArrowDown: 'down',
  ArrowRight: 'right',
};

/** These two are not a state anybody holds: they fire on the way down, once. */
const ACTIONS: Record<string, PlayerAction> = {
  KeyR: 'reload',
  Space: 'dash',
};

/**
 * Keyboard and mouse into the same shape the player carries. Only what changed
 * is reported: holding a key still is not news, and the server keeps the last
 * word it heard.
 */
export function listenToInput(
  target: HTMLElement,
  events: {
    onChange: (input: Partial<PlayerInput>) => void;
    onAction: (action: PlayerAction) => void;
  },
) {
  const state: Partial<PlayerInput> = {};

  window.addEventListener('keydown', event => setKey(event, true));
  window.addEventListener('keyup', event => setKey(event, false));
  target.addEventListener('mousedown', () => change({ shoot: true }));
  window.addEventListener('mouseup', () => change({ shoot: false }));
  target.addEventListener('mousemove', event => aimAt(event));
  target.addEventListener('contextmenu', event => event.preventDefault());

  // A tab that loses focus never sees the keyup, and the player walks into a
  // wall until it comes back.
  window.addEventListener('blur', releaseEverything);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) releaseEverything();
  });

  return { current: () => ({ ...state }) };

  function setKey(event: KeyboardEvent, pressed: boolean) {
    const key = KEYS[event.code];
    if (key) change({ [key]: pressed });

    const action = ACTIONS[event.code];
    if (!action || !pressed) return;

    // Space scrolls the page, and a held key repeats: neither is a new order.
    event.preventDefault();
    if (!event.repeat) events.onAction(action);
  }

  function aimAt(event: MouseEvent) {
    change({
      aim: {
        x: event.clientX - window.innerWidth / 2,
        y: event.clientY - window.innerHeight / 2,
      },
    });
  }

  function releaseEverything() {
    const released: Partial<PlayerInput> = {};

    for (const key of ['up', 'down', 'left', 'right', 'shoot'] as const) {
      if (state[key]) released[key] = false;
    }

    if (Object.keys(released).length) change(released);
  }

  function change(values: Partial<PlayerInput>) {
    const news: Partial<PlayerInput> = {};

    for (const [key, value] of Object.entries(values)) {
      if (isSame(state[key as keyof PlayerInput], value)) continue;

      Object.assign(state, { [key]: value });
      Object.assign(news, { [key]: value });
    }

    if (Object.keys(news).length) events.onChange(news);
  }
}

function isSame(a: unknown, b: unknown) {
  if (a === b) return true;
  if (!isPoint(a) || !isPoint(b)) return false;
  return a.x === b.x && a.y === b.y;
}

function isPoint(value: unknown): value is { x: number; y: number } {
  return typeof value === 'object' && value !== null && 'x' in value;
}
